import { useMutation, useQueryClient } from 'react-query';
import { ContractService as cs } from '../api/ContractService';
import { Contract } from '../api/protosCompiled/contract/contract_pb';
import { useStore } from 'zustand';
import { useUnityBuildStore } from '../stores/store';

export const useCreateContractMutation = () => {
  const queryClient = useQueryClient();
  const { selectedProject } = useStore(useUnityBuildStore);

  return useMutation((data: Contract.AsObject) => cs.createContract(data), {
    onSuccess: () => {
      queryClient.invalidateQueries(['contracts', selectedProject.id]);
    },
    onError: error => {
      console.error('Error creating contract:', error);
      // You can add a toast notification here if you have a notification system
    },
  });
};

export const useUpdateContractMutation = () => {
  const queryClient = useQueryClient();
  const { selectedProject } = useStore(useUnityBuildStore);

  return useMutation((data: Contract.AsObject) => cs.updateContract(data), {
    onSuccess: () => {
      queryClient.invalidateQueries(['contracts', selectedProject.id]);
    },
    onError: error => {
      console.error('Error updating contract:', error);
      // You can add a toast notification here if you have a notification system
    },
  });
};

export const useDeleteContractMutation = () => {
  const queryClient = useQueryClient();
  const { selectedProject } = useStore(useUnityBuildStore);

  return useMutation((data: Contract.AsObject) => cs.deleteContract(data.contractid), {
    // Optimistic update - immediately remove from cache
    onMutate: async deletedContract => {
      const queryKey = ['contracts', selectedProject.id];

      // Cancel any outgoing refetches
      await queryClient.cancelQueries(queryKey);

      // Snapshot the previous value
      const previousContracts = queryClient.getQueryData(queryKey);

      // Optimistically update to remove the contract
      queryClient.setQueryData(queryKey, (old: Contract.AsObject[] | undefined) => {
        if (!old) return [];
        return old.filter(contract => contract.contractid !== deletedContract.contractid);
      });

      // Return a context object with the snapshotted value
      return { previousContracts, queryKey };
    },

    // If the mutation fails, use the context returned from onMutate to roll back
    onError: (err, deletedContract, context) => {
      console.error('Error deleting contract:', err);
      if (context?.previousContracts) {
        queryClient.setQueryData(context.queryKey, context.previousContracts);
      }
    },

    // Always refetch after error or success to ensure we have the latest data
    onSettled: () => {
      queryClient.invalidateQueries(['contracts', selectedProject.id]);
    },
  });
};
